import { DownloadIcon } from "assets/icons";
import { If } from "components";
import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { useBoolean, useSearchParameter } from "hooks";
import { BoxShadowLayout, ContentLayout } from "layouts";
import { QRCodeCanvas } from "qrcode.react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Button, Pagination, SearchInput } from "ui";

import { useOrdersDetailsByIds } from "../../apis";
import { OrderModal, OrdersTable, OrdersTableFilter } from "../../components";
import { downloadOrderPdf } from "../../helpers";
import {
  useOrdersList,
  useOrdersAndReturnsListRealTimeUpdate
} from "../../hooks";
import { Order } from "../../orders.types";

import styles from "./Orders.module.scss";

const Orders = () => {
  const { t } = useTranslation();
  const {
    mutateAsync: getOrdersDetailsByIds,
    isLoading: isOrdersForDownloadLoading
  } = useOrdersDetailsByIds();
  const {
    value: isFilterModalOpen,
    toggle: toggleFilterModal,
    setFalse: closeFilterModal
  } = useBoolean(false);

  const [openedOrderId, setOpenedOrderId] = useSearchParameter("orderId");

  const { pathname } = useLocation();
  const isCompanyPortal = pathname.startsWith("/company");

  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);

  const {
    orders,
    searchValue,
    setSearchValue,
    isLoading,
    sorting,
    page,
    setPage,
    totalItems,
    setSorting,
    refetchOrders,
    drivers,
    isDriversLoading,
    locations,
    isLocationsLoading
  } = useOrdersList();

  const onCloseOrderModal = () => {
    setOpenedOrderId(null);
  };

  useOrdersAndReturnsListRealTimeUpdate();

  return (
    <ContentLayout
      title={t("Orders")}
      action={
        selectedOrderIds.length !== 0 && (
          <>
            <Button
              type="button"
              className={styles.downloadButton}
              title={`${t("Download QR & Details Sheet")} (${selectedOrderIds.length})`}
              icon={<DownloadIcon width={24} height={24} />}
              variant="default"
              loading={isOrdersForDownloadLoading}
              onClick={async () => {
                const orderDetailsForDownload =
                  await getOrdersDetailsByIds(selectedOrderIds);

                downloadOrderPdf(orderDetailsForDownload);
              }}
            />

            {selectedOrderIds.map((orderId) => (
              <QRCodeCanvas
                key={orderId}
                value={orderId}
                id={`qr-${orderId}`}
                style={{ display: "none" }}
              />
            ))}
          </>
        )
      }
    >
      <BoxShadowLayout className={styles.pageContent}>
        <div className={styles.pageContent_tableHeaderControls}>
          <SearchInput
            value={searchValue}
            onChange={setSearchValue}
            placeholder={`${t("Search")}...`}
          />
          <OrdersTableFilter
            isLoading={isLoading}
            isOpen={isFilterModalOpen}
            onClose={closeFilterModal}
            onToggle={toggleFilterModal}
            refetchOrders={refetchOrders}
            drivers={drivers}
            isDriversLoading={isDriversLoading}
            locations={locations}
            isLocationsLoading={isLocationsLoading}
          />
        </div>

        <OrdersTable
          ordersList={orders ?? []}
          isLoading={isLoading}
          sorting={sorting}
          setSorting={setSorting}
          refetch={refetchOrders}
          selectedOrdersIds={selectedOrderIds}
          setSelectedOrdersIds={setSelectedOrderIds}
          onAssignDriver={
            isCompanyPortal
              ? (order: Order) => setOpenedOrderId(order.id)
              : undefined
          }
        />

        <Pagination
          currentPage={page}
          onChange={setPage}
          perPage={GET_LIST_REQUEST_DEFAULT_LIMIT}
          totalItems={totalItems}
        />
      </BoxShadowLayout>

      <If condition={openedOrderId}>
        <OrderModal
          orderId={String(openedOrderId)}
          isOpen={Boolean(openedOrderId)}
          onClose={onCloseOrderModal}
          refetchOrders={refetchOrders}
        />
      </If>
    </ContentLayout>
  );
};

export default Orders;
